import axios from 'axios';
import { getOneRosterFileApiBaseUrl } from './constants';
import { NotificationTypes, showNotification } from '../components/Notifications';
import { PresignedUrlResponse, JobStatusResponse, FileUploadStatus } from './types';
import { MAX_FILE_SIZE_BYTES, EXPECTED_FILE_NAME, ACCEPTED_FILE_EXTENSION } from './constants';

/**
 * Gets presigned URL for file upload from the server
 */
export const getPresignedUrl = async (): Promise<PresignedUrlResponse | null> => {
  const baseUrl = getOneRosterFileApiBaseUrl();
  const url = `${baseUrl}/oneroster/job/generate-url`;

  try {
    const response = await axios.get(url, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    return null;
  }
};

/**
 * Uploads the selected file to the presigned URL
 */
export const uploadFile = async (
  file: File,
  onUploadStart: () => void,
  onUploadSuccess: (jobId: string) => void,
  onUploadError: (error: string) => void,
): Promise<string | null> => {
  console.log('Uploading file:', file);

  if (!file) {
    showNotification(NotificationTypes.error, 'Upload File', 'Please select a file to upload.');
    return null;
  }

  onUploadStart();

  try {
    const response = await getPresignedUrl();
    if (!response) {
      onUploadError('Failed to get presigned URL');
      return null;
    }

    const { presignedUrl, jobId } = response;

    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': 'application/zip',
      },
    });

    console.log('File uploaded successfully');
    onUploadSuccess(jobId);
    return jobId;
  } catch (error) {
    console.error('Error uploading file:', error);
    onUploadError('Error uploading file');
    return null;
  }
};

/**
 * Validates the selected file
 */
export const validateFile = (file: File): string | null => {
  // Check file size
  if (file.size >= MAX_FILE_SIZE_BYTES) {
    return 'The file size exceeds the maximum limit of 1GB.';
  }

  const fileName = file.name;

  console.log('Validating file:', fileName);
  // Check file name starts with expected prefix
  const expectedFileName = `${EXPECTED_FILE_NAME}.${ACCEPTED_FILE_EXTENSION}`;
  if (fileName.toLowerCase() !== expectedFileName.toLowerCase()) {
    return `Wrong file name. File must be named ${EXPECTED_FILE_NAME}.${ACCEPTED_FILE_EXTENSION}`;
  }
  if (!fileName.endsWith(ACCEPTED_FILE_EXTENSION)) {
      return 'Wrong file format. Only .zip files are accepted.';
  }

  return null; // No errors
};

/**
 * Fetches the latest file upload status from the server
 */
export const fetchFileUploadStatus = async (): Promise<Partial<FileUploadStatus>> => {
  try {
    const baseUrl = getOneRosterFileApiBaseUrl();
    const response = await axios.get(`${baseUrl}/oneroster/job/latest/status`, {
      withCredentials: true,
    });

    console.log('File upload status response:', response.data);

    if (response.data) {
      return {
        lastUpload: response.data.uploadDate ? new Date(response.data.uploadDate) : null,
        uploadMessage:
          response.data.status === 'COMPLETED'
            ? 'File processed successfully'
            : response.data.status || 'No recent uploads',
      };
    }

    return {
      lastUpload: null,
      uploadMessage: 'No recent uploads',
    };
  } catch (error) {
    console.error('Error fetching file upload status:', error);
    return {
      lastUpload: null,
      uploadMessage: 'No recent uploads',
    };
  }
};

/**
 * Fetches job status by job ID
 */
export const fetchJobStatus = async (jobId: string): Promise<JobStatusResponse | null> => {
  try {
    const response = await axios.get(`${getOneRosterFileApiBaseUrl()}/oneroster/job/${jobId}/status`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching job status:', error);
    throw error;
  }
};
